import { useEffect, useState } from "react";
import { Cloud, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import api from "@/api/baseApi";
import toast from "react-hot-toast";
import { FileInput } from "@/components/ui/FileInput";
import { toWords } from "@/utils/TowordsConfig";

interface DepositProps {
  selectedRows: any[] | null;
  onDeposit: () => void;
}

const Deposit: React.FC<DepositProps> = ({ selectedRows, onDeposit }) => {
  const totalBalance = selectedRows?.reduce(
    (acc, row) => acc + parseFloat(row.amount),
    0
  );
  console.log(totalBalance);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(totalBalance);
  const [depositType, setDepositType] = useState("office");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [submitting, setSubmitting] = useState(false);
  const [reciept, setReciept] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [bank, setBank] = useState("bnp");
  const [amountLetter, setAmountLetter] = useState(
    toWords.convert(totalBalance, { currency: true })
  );

  const handleDeposit = async () => {
    setSubmitting(true);

    let formData = new FormData();
    formData.append("image", reciept as File);
    formData.append("path", "collect-reciepts");
    formData.append("description", "deposited by collector ");
    await api
      .post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(async (res) => {
        console.log(res.data);
        const deposit = {
          collectionIds: selectedRows?.map((row) => row.id),
          depositedMethod: depositType,
          depositedType: paymentMethod,
          depositedBank: bank,
          wilaya: "Alger",
          status: "Payment Deposited",
          amount: amount,
          amountLetter: amountLetter,
          date: new Date().toISOString(),
          fileId: res.data.id.toString(),
        };
        await api
          .patch("/collector/orders/deposit", deposit)
          .then((res) => {
            console.log("res :", res);

            toast.success("Amount deposited successfully");
            onDeposit();
            setAmountLetter("");
            setBank("bnp");
            setReciept(null);
            setImagePreview(null);
            setOpen(false);
          })
          .catch((error) => {
            console.log("err in deposit: ", error);
            toast.error("Something went wrong");
          });
      })
      .catch((error) => {
        console.log(error);
        toast.error("File upload failed");
      });

    setSubmitting(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setReciept(file);

    // Generate a preview URL if the file is an image
    if (file && file.type.startsWith("image/")) {
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    } else {
      setImagePreview(null);
    }
  };
  const handleRemoveFile = () => {
    setReciept(null);
    setImagePreview(null);
  };

  useEffect(() => {
    setAmount(totalBalance);
    setAmountLetter(toWords.convert(totalBalance, { currency: true }));
  }, [selectedRows, totalBalance]); // Ensure it recalculates if `totalBalance` changes

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const amountValue = Number(e.target.value);
    setAmount(amountValue);

    // Convert updated amount to words
    const amountInWords = toWords.convert(amountValue, { currency: true });
    setAmountLetter(amountInWords);
  };

  console.log("amountValue", amount);
  console.log("amountInWords", amountLetter);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        disabled={selectedRows?.length === 0}
        className={`
        ${
          selectedRows?.length === 0
            ? "pointer-events-none bg-[#00BC39]/50"
            : "bg-[#00BC39]"
        }
        px-8 text-[16px] text-white py-2  font-normal   rounded-md  hover:bg-[#00BC39]/90 cursor-pointer hover:scale-[1.05] transition ease-in-out duration-200`}
      >
        Deposit
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px] h-[90%] overflow-y-scroll p-12">
        <DialogHeader>
          <DialogTitle className="text-xl">Deposit</DialogTitle>
        </DialogHeader>
        <div className="grid gap-6 pt-4">
          <div className="flex flex-col gap-4">
            <ToggleGroup
              type="single"
              value={depositType}
              onValueChange={(value) => value && setDepositType(value)}
              className="flex justify-between"
            >
              <ToggleGroupItem
                value="office"
                className="flex-1 border border-primary rounded-md data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
              >
                Office
              </ToggleGroupItem>
              <ToggleGroupItem
                value="bank"
                className="flex-1 border border-primary rounded-md data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
              >
                Bank
              </ToggleGroupItem>
            </ToggleGroup>

            <ToggleGroup
              type="single"
              value={paymentMethod}
              onValueChange={(value) => value && setPaymentMethod(value)}
              className="flex justify-between"
            >
              <ToggleGroupItem
                value="cash"
                className="flex-1  border border-primary rounded-md data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
              >
                Cash
              </ToggleGroupItem>
              <ToggleGroupItem
                value="cheque"
                className="flex-1 border border-primary rounded-md data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
              >
                Chéque
              </ToggleGroupItem>
            </ToggleGroup>
          </div>

          {depositType === "bank" && (
            <div className="grid gap-2">
              <Label htmlFor="bank">Bank</Label>
              <Select value={bank} onValueChange={setBank}>
                <SelectTrigger id="bank">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bnp">BNP Paribas</SelectItem>
                  <SelectItem value="societe">Société Générale</SelectItem>
                  <SelectItem value="credit">Crédit Agricole</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid gap-2">
            <Label htmlFor="balance">Total balance</Label>
            <Input
              disabled
              id="balance"
              type="number"
              defaultValue={totalBalance.toFixed(2)}
              placeholder="35000 DZD"
              onChange={handleAmountChange}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="amount-letter">Deposit amount in letter</Label>
            <Input
              id="amountLetter"
              value={amountLetter} // Use the amountLetter state to display the converted value
              className="col-span-3 text-primary"
              readOnly // Make this field read-only
            />
          </div>
          <div className="flex items-center justify-between">
            <Label
              htmlFor="receipt"
              className="text-left text-sm font-normal text-[#717171]"
            >
              Attach a receipt
            </Label>
            <FileInput id="reciept" onChange={handleFileChange} />
          </div>
          {imagePreview && (
            <div className="flex flex-col items-center mt-2 space-y-2">
              <img
                src={imagePreview}
                alt="File preview"
                className="object-cover w-32 h-32 rounded"
              />
              <button
                type="button"
                onClick={handleRemoveFile}
                className="text-sm text-red-500 underline hover:text-red-700"
              >
                Remove
              </button>
            </div>
          )}

          <Button
            disabled={submitting}
            onClick={handleDeposit}
            className="w-full py-4 mt-8 bg-[#4CAF50] hover:bg-[#45a049]"
          >
            {submitting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <span>Deposit</span>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Deposit;
